import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { UserActions } from 'store/User/Actions';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { performLogout } from 'utils/auth/performLogout';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';
import { hasRole, UserRole } from 'utils/userRoleHelper';

import Icon, { IconEnum } from 'components/common/Icon';

type TabId = 'moduly' | 'marketplace' | 'moje' | 'analityka' | 'konto' | 'posrednik';

interface TabDef {
    id: TabId;
    icon: IconEnum;
    title: string;
}

interface ItemDef {
    label: string;
    icon: IconEnum;
    onClick: () => void;
    badge?: string;
}

const BottomBar = styled.nav`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(72px + env(safe-area-inset-bottom, 0px));
    background-color: var(--color-white);
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.10);
    display: flex;
    align-items: stretch;
    z-index: 103;
    padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const TabButton = styled.button<{ $active: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: ${({ $active }) => $active ? 'var(--color-nav-active)' : 'none'};
    border: none;
    cursor: pointer;
    padding: 8px 4px;
    border-radius: 10px;
    transition: background-color 0.15s;

    &:hover, &:active {
        background-color: ${({ $active }) => $active ? 'var(--color-nav-active)' : 'var(--color-alt-second)'};
    }
`;

const TabIcon = styled.div<{ $active: boolean }>`
    opacity: ${({ $active }) => $active ? 1 : 0.55};
    transition: opacity 0.15s;

    span {
        width: 28px;
        height: 28px;
    }

    img {
        width: 28px;
        height: 28px;
    }
`;

const TabLabel = styled.span`
    font-size: 10px;
    font-weight: 500;
    color: var(--color-primary);
    transition: color 0.15s;
    line-height: 1;

    &.active {
        color: var(--color-alt);
        font-weight: 600;
    }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.35);
    z-index: 101;
    animation: mobileOverlayIn 0.2s ease;

    @keyframes mobileOverlayIn {
        from { opacity: 0; }
        to   { opacity: 1; }
    }
`;

const DrawerSheet = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 78vh;
    background-color: var(--color-white);
    border-radius: 20px 20px 0 0;
    z-index: 102;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.10);
    animation: mobileDrawerUp 0.28s cubic-bezier(0.32, 0.72, 0, 1);

    @keyframes mobileDrawerUp {
        from { transform: translateY(100%); }
        to   { transform: translateY(0); }
    }
`;

const DragHandleWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 60px 8px;
    cursor: pointer;
    flex-shrink: 0;
`;

const DragHandle = styled.div`
    width: 36px;
    height: 4px;
    background-color: var(--color-background);
    border-radius: 2px;
`;

const DrawerHeader = styled.div`
    padding: 14px 20px 6px;
    flex-shrink: 0;
`;

const DrawerTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 2px;
`;

const DrawerSubtitle = styled.p`
    font-size: 12px;
    color: var(--color-alt-second-10);
    margin: 0;
`;

const DrawerScrollArea = styled.div`
    overflow-y: auto;
    padding: 10px 16px calc(72px + env(safe-area-inset-bottom, 0px));
    flex: 1;
    scrollbar-width: thin;
    scrollbar-color: var(--color-scroll) transparent;
`;

const ItemIconWrap = styled.div`
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 14px;
`;

const ItemLabel = styled.span`
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-primary);
    transition: color 0.15s;
`;

const ItemBadge = styled.span`
    background-color: var(--color-alt);
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 20px;
    margin-left: 8px;
    margin-right: 4px;
`;

const ItemChevron = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    line-height: 1;
    opacity: 0.55;
    transition: opacity 0.15s;

    .icon, img {
        width: 14px;
        height: 14px;
    }
`;

const DrawerItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 11px 14px;
    margin-bottom: 8px;
    background-color: var(--color-alt-second);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.15s;

    &:hover, &:active {
        background-color: var(--color-alt);
        color: #ffff;

        ${ItemLabel} {
            color: #ffff;
        }

        ${ItemChevron} {
            opacity: 0.85;
        }

        img {
            filter: brightness(0) invert(1);
        }
    }
`;

interface NavigationMobileProps {
    activeTab: TabId | null;
    setActiveTab: (tab: TabId | null) => void;
}

const NavigationMobile: FC<NavigationMobileProps> = ({ activeTab, setActiveTab }) => {
    const { user, dispatch } = useUser();
    const navigate = useNavigate();
    const platform = usePlatform();
    const isDarkMode = user.isDarkMode;
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);
    const hasPredictPriceRole = hasRole(UserRole.PREDICT_PRICE, user.userRole);
    const isWordpressUser = user.isWordpressUser;
    const scrollRef = useRef<HTMLDivElement>(null);
    const touchStartY = useRef<number>(0);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [activeTab]);

    const close = useCallback(() => setActiveTab(null), [setActiveTab]);

    const go = useCallback((path: string) => {
        navigate(path);
        close();
    }, [navigate, close]);

    const openExternal = useCallback((url: string) => {
        window.open(url, '_blank');
        close();
    }, [close]);

    const logout = useCallback(() => performLogout({
        dispatch,
        isWordpressUser,
        platform,
        onAfterDispatch: close
    }), [
        dispatch,
        close,
        isWordpressUser,
        platform
    ]);

    const TABS = useMemo<TabDef[]>(() => [
        { id: 'moduly', icon: IconEnum.HOUSE, title: 'Moduły' },
        { id: 'marketplace', icon: IconEnum.USER, title: 'Marketplace' },
        { id: 'moje', icon: IconEnum.HEART_NAV, title: 'Moje' },
        { id: 'analityka', icon: IconEnum.CHART, title: 'Analityka' },
        { id: 'konto', icon: IconEnum.COG, title: 'Konto' },
        ...user.isInOrganization
            ? [{ id: 'posrednik' as TabId, icon: IconEnum.BRIEFCASE, title: 'Strefa pośrednika' }]
            : []
    ], [user.isInOrganization]);

    const itemsByTab = useMemo<Record<TabId, ItemDef[]>>(() => ({
        moduly: [
            { label: 'Sprzedaż', icon: IconEnum.HOUSE, onClick: () => go('/') },
            { label: 'Wynajem', icon: IconEnum.HOUSE_CIRCLE, onClick: () => go('/rent') },
            { label: 'Licytacje komornicze', icon: IconEnum.GAVEL, onClick: () => go('/auction') },
            { label: 'Przetargi', icon: IconEnum.BUILDING_COLUMNS, onClick: () => go('/tender') },
            { label: 'Przetargi lokalne', icon: IconEnum.MAP_PIN, onClick: () => go('/cooperative') },
            { label: 'Informacje o zadłużeniach', icon: IconEnum.TRIANGLE_WARNING, onClick: () => go('/bailiff-notices') }
        ],
        marketplace: [
            { label: 'Ogłoszenia użytkowników', icon: IconEnum.USER, onClick: () => go('/user') },
            { label: 'Moje ogłoszenia', icon: IconEnum.BOOKMARK, onClick: () => go('/my-offers') },
            { label: 'Dodaj ogłoszenie', icon: IconEnum.PLUS_CIRCLE, onClick: () => go('/add-offer') },
            { label: 'Wiadomości', icon: IconEnum.MESSAGE, onClick: () => go('/messages') }
        ],
        moje: [
            { label: 'Ulubione', icon: IconEnum.HEART_NAV, onClick: () => go('/favorites') },
            { label: 'Flipchat', icon: IconEnum.CHAT_LINES, onClick: () => go('/chatbot-messages') }
        ],
        analityka: [
            { label: 'Analiza cen ofertowych', icon: IconEnum.CHART, onClick: () => go('/analysis') },
            ...hasPredictPriceRole
                ? [{ label: 'Wycena mieszkań', icon: IconEnum.SCALE, onClick: () => go('/property-valuation') }]
                : [],
            ...hasCalculationRole
                ? [
                    { label: 'Kalkulator Flipa', icon: IconEnum.CALCULATOR, onClick: () => go('/calculator'), badge: 'NEW' },
                    { label: 'Panel Analityczny', icon: IconEnum.GRID_2X2, onClick: () => go('/analytic-panel'), badge: 'NEW' }
                ]
                : [],
            ...isWordpressUser && platform === Platform.Web
                ? [{ label: 'Ceny mieszkań', icon: IconEnum.CIRCLE_DOLLAR, onClick: () => openExternal('https://ceny.investoro.pl/') }]
                : []
        ],
        konto: [
            ...isWordpressUser && platform === Platform.Web
                ? [{ label: 'Panel użytkownika', icon: IconEnum.USER, onClick: () => openExternal('https://investoro.pl/userpanel/') }]
                : [],
            { label: 'Ustawienia konta', icon: IconEnum.COG, onClick: () => go('/my-settings') },
            ...hasCalculationRole
                ? [{ label: 'Ustawienia kalkulatora', icon: IconEnum.CALCULATOR, onClick: () => go('/calculator-settings') }]
                : [],
            {
                label: isDarkMode ? 'Jasny motyw' : 'Ciemny motyw',
                icon: IconEnum.MOON,
                onClick: () => {
                    UserActions.changeTheme(dispatch, !isDarkMode);
                    close();
                }
            },
            { label: 'Jak używać', icon: IconEnum.QUESTION_CIRCLE, onClick: () => openExternal('https://investoro.pl/jak-uzywac/') },
            ...isWordpressUser && platform === Platform.Web
                ? [{ label: 'Pomoc / Zgłoszenia', icon: IconEnum.ARROW_UP_RIGHT, onClick: () => openExternal('https://investoro.pl/wsparcie') }]
                : [],
            ...platform === Platform.Web
                ? [
                    { label: 'Pobierz aplikację Android', icon: IconEnum.ANDROID, onClick: () => openExternal('https://play.google.com/store/apps/details?id=com.investoro.app') },
                    { label: 'Pobierz aplikację iOS', icon: IconEnum.IOS, onClick: () => openExternal('https://apps.apple.com/pl/app/investoro/id6740319885') }
                ]
                : [],
            { label: 'Wyloguj się', icon: IconEnum.LEAVE, onClick: logout }
        ],
        posrednik: [
            { label: 'Moje pozyski', icon: IconEnum.BRIEFCASE, onClick: () => go('/broker') },
            { label: 'Zarządzanie agentami', icon: IconEnum.PEOPLES_3, onClick: () => go('/my-settings#panel-posrednika') }
        ]
    }), [
        hasCalculationRole,
        hasPredictPriceRole,
        isWordpressUser,
        platform,
        isDarkMode,
        go,
        openExternal,
        logout,
        dispatch,
        close
    ]);

    const activeTabDef = TABS.find((t) => t.id === activeTab);
    const items = activeTab ? itemsByTab[activeTab] : [];

    return (
        <>
            {activeTab &&
                <>
                    <Overlay onClick={close} />
                    <DrawerSheet className="dark-mode-icons">
                        <DragHandleWrapper
                            onClick={close}
                            onTouchStart={(e: React.TouchEvent) => {
                                touchStartY.current = e.touches[0].clientY;
                            }}
                            onTouchEnd={(e: React.TouchEvent) => {
                                if (e.changedTouches[0].clientY - touchStartY.current > 60) close();
                            }}
                        >
                            <DragHandle />
                        </DragHandleWrapper>
                        <DrawerHeader>
                            <DrawerTitle>{activeTabDef?.title}</DrawerTitle>
                            <DrawerSubtitle>Wybierz akcję lub wyświetl szczegóły.</DrawerSubtitle>
                        </DrawerHeader>
                        <DrawerScrollArea ref={scrollRef}>
                            {items.map((item) => <DrawerItem key={item.label} onClick={item.onClick}>
                                <ItemIconWrap>
                                    <Icon icon={item.icon} />
                                </ItemIconWrap>
                                <ItemLabel>{item.label}</ItemLabel>
                                {item.badge && <ItemBadge>{item.badge}</ItemBadge>}
                                <ItemChevron aria-hidden>
                                    <Icon icon={IconEnum.ANGLE_RIGHT} />
                                </ItemChevron>
                            </DrawerItem>)}
                        </DrawerScrollArea>
                    </DrawerSheet>
                </>
            }

            <BottomBar>
                {TABS.map((tab) => <TabButton
                    key={tab.id}
                    $active={activeTab === tab.id}
                    onClick={() => setActiveTab(activeTab === tab.id ? null : tab.id)}
                    title={tab.title}
                >
                    <TabIcon $active={activeTab === tab.id}>
                        <Icon icon={tab.icon} />
                    </TabIcon>
                    <TabLabel className={clsx({ active: activeTab === tab.id })}>
                        {tab.title}
                    </TabLabel>
                </TabButton>)}
            </BottomBar>
        </>
    );
};

export type { TabId };
export default NavigationMobile;
